// Card actions and sheets shared by C (chat) and T (Today), phone v2 (F_Chat, F_Today, F_Edit, F_Phone):
//   tick(card, 'C' | 'T')     Mark done. A big reward (isBigReward: lit a day, perfect day, level up) opens R past the
//                             card beat (rbeat 1, F_Phone); otherwise the light celebration: the card stamps in place,
//                             toast "{short}: done · +N points" with Undo (undoneCard), reward cleared (rewardSeen) so
//                             R never pops up later. From chat the server adds the 'done' note (from: 'chat').
//   stuckOn(id)               Stuck is talked through in chat (stuckChat + C); no QS1 panel any more.
//   <TickButton/>             the tick circle on a row
//   <CardMore/>               Mark done · Edit · Later under a card (Today NOW card, the card sheet)
//   <CardSheet/>              tap a card in chat: Start / Stuck / Remove + Mark done / Edit / Later
//   <EditSheet/>              F_Edit: WHAT / WHEN (XTime) / HOW LONG / Save / "Take it off the plan" (plan rows)
//   <LaterSheet/>             "Do it later": In 1 hour / After lunch / Tonight / Tomorrow
//   <TodaySheet/>             today's cards, from the middle of the chat's next-card strip
import type { JSX } from 'preact';
import { useRef, useState } from 'preact/hooks';
import type { Card, LaterWhen, Reward } from '@shared/types';
import { LATER_CHIPS } from '@shared/content/lines';
import { LATER_SAID } from '@shared/content/stuck';
import { isBigReward, taskShort } from '@shared/rules';
import { useApp, doneCard, undoneCard, rewardSeen, toast, dismissToast, stuckChat, laterCard, removeCard } from '../../store';
import { go, routerStore } from '../../router';
import { haptic } from '../../native';
import { BottomSheet, CardButtons, Icon, PrimaryButton, XTime } from '../../components';
import { work } from './util';
import s from './cards.module.css';

export type CardFrom = 'C' | 'T';

/** Mark a card done. Resolves with the reward (null on failure or when the card was not to do). */
export async function tick(card: Card, from: CardFrom, opts: { onUndo?: () => void } = {}): Promise<Reward | null> {
  if (card.st !== 'todo') return null;
  haptic('light');
  const rw = await doneCard(card.id, from === 'C' ? { from: 'chat' } : {});
  if (!rw) return null;
  if (isBigReward(rw)) { go('R', { cur: card.id, rbeat: 1, from }); return rw; }
  void rewardSeen();
  haptic('success');
  toast(`${taskShort(card.title)}: done · +${rw.gained} points`, {
    undo: () => { dismissToast(); opts.onUndo?.(); void undoneCard(card.id); },
  });
  return rw;
}

/** Stuck on a card, from anywhere: "I'm stuck on …" and her six reasons in chat. */
export function stuckOn(id: string) {
  void stuckChat(id);
  if (routerStore.get().s !== 'C') go('C');
}

/** Sheets on the phone use the prototype's 16px side padding (the time and edit sheets keep BottomSheet's 24). */
const PAD16: JSX.CSSProperties = { padding: '12px calc(16px + var(--xm-sar)) calc(var(--xm-bottom) + 16px) calc(16px + var(--xm-sal))', gap: 12 };

export function TickButton({ done, title, onTick, w = 44, h = 44, class: cls, style }: {
  done: boolean; title: string; onTick: () => void; w?: number; h?: number; class?: string; style?: JSX.CSSProperties;
}) {
  // The stamp pops only when the card is ticked while this row is on screen, not for every done row on mount.
  const was = useRef(done);
  const pop = done && !was.current;
  return (
    <button
      type="button" class={[s.tick, cls].filter(Boolean).join(' ')} style={{ width: w, height: h, ...style }}
      aria-label={`${done ? 'Done' : 'Mark done'}: ${title}`} aria-pressed={done ? 'true' : 'false'}
      onClick={() => { if (!done) onTick(); }}
    >
      <span class={s.ring} data-done={done ? 'true' : 'false'} data-pop={pop ? 'true' : 'false'} aria-hidden="true">
        <Icon name="check" size={14} strokeWidth={3} color={done ? 'var(--on)' : 'transparent'} />
      </span>
    </button>
  );
}

export function CardMore({ title, onDone, onEdit, onLater, style }: {
  title: string; onDone: () => void; onEdit: () => void; onLater: () => void; style?: JSX.CSSProperties;
}) {
  return (
    <div class={s.more} style={style}>
      <button type="button" class={s.moreBtn} data-accent="true" aria-label={`Mark done: ${title}`} onClick={onDone}>
        <span class={s.moreRing} aria-hidden="true"><Icon name="check" size={12} strokeWidth={3.2} color="var(--acc)" /></span>
        <span>Mark done</span>
      </button>
      <button type="button" class={s.moreBtn} aria-label={`Edit: ${title}`} onClick={onEdit}>
        <Icon name="pencil" size={17} color="#CFC8BC" /><span>Edit</span>
      </button>
      <button type="button" class={s.moreBtn} aria-label={`Do it later: ${title}`} onClick={onLater}>
        <Icon name="clock" size={17} color="#CFC8BC" /><span>Later</span>
      </button>
    </div>
  );
}

/** The card sheet (F_Chat cardOpen). Closes itself before every action. */
export function CardSheet({ card, from, onClose, onEdit, onLater }: {
  card: Card | null; from: CardFrom; onClose: () => void; onEdit: (c: Card) => void; onLater: (c: Card) => void;
}) {
  const c = card;
  return (
    <BottomSheet open={!!c} onClose={onClose} label={c ? `Card: ${c.title}` : 'Card'} handle style={PAD16}>
      {c && (
        <>
          <div class={s.sheetHead}>
            <p class={s.sheetLabel}>{`${c.st === 'done' ? 'DONE' : 'CARD'} · ${c.time} · ${c.min} MIN`}</p>
            <p class={s.sheetTitle}>{c.title}</p>
          </div>
          <CardButtons
            title={c.title}
            onStart={() => { onClose(); work(c.id, null, from); }}
            onStuck={() => { onClose(); stuckOn(c.id); }}
            onRemove={() => { onClose(); void removeCard(c.id); }}
          />
          <CardMore
            title={c.title}
            onDone={() => { onClose(); void tick(c, from); }}
            onEdit={() => { onClose(); onEdit(c); }}
            onLater={() => { onClose(); onLater(c); }}
          />
        </>
      )}
    </BottomSheet>
  );
}

export interface CardValues { title: string; time: string; min: number }
const MINS = [5, 10, 15, 25, 45];

/** F_Edit "Change this card". `plan`: a row of a plan card still waiting for Looks good ("Take it off the plan"). */
export function EditSheet({ open, value, plan, onClose, onSave, onRemove }: {
  open: boolean; value: CardValues | null; plan?: boolean; onClose: () => void; onSave: (v: CardValues) => void; onRemove?: () => void;
}) {
  return (
    <BottomSheet open={open && !!value} onClose={onClose} label="Change this card" handle>
      {value && <EditBody value={value} plan={!!plan} onSave={onSave} onRemove={onRemove} />}
    </BottomSheet>
  );
}

/** Mounted each time the sheet opens, so the fields start from the card. */
function EditBody({ value, plan, onSave, onRemove }: { value: CardValues; plan: boolean; onSave: (v: CardValues) => void; onRemove?: () => void }) {
  const [t, setT] = useState(value.title);
  const [tm, setTm] = useState(value.time);
  const [mn, setMn] = useState(value.min);
  const list = MINS.includes(mn) ? MINS : [...MINS, mn].sort((a, b) => a - b);
  const save = () => {
    const title = t.trim();
    if (!title) { toast('Write what the card says first.'); return; }
    onSave({ title, time: tm, min: mn });
  };
  return (
    <>
      <p class={s.sheetH}>Change this card</p>
      <label class={s.field}>
        <span class={s.fieldLabel}>WHAT</span>
        <input
          type="text" class={s.input} aria-label="What the card says" value={t} maxLength={120} enterKeyHint="done"
          onInput={(e) => setT((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
        />
      </label>
      <div class={s.field} style={{ gap: 4 }}>
        <span class={s.fieldLabel}>WHEN</span>
        <XTime value={tm} onPick={setTm} bg="#1C1916" w={342} />
      </div>
      <div class={s.field} style={{ gap: 8 }}>
        <span class={s.fieldLabel} id="xm-edit-min">HOW LONG</span>
        <div class={s.mins} role="group" aria-labelledby="xm-edit-min">
          {list.map((m) => (
            <button key={m} type="button" class={s.min} aria-pressed={m === mn ? 'true' : 'false'} onClick={() => setMn(m)}>{`${m} min`}</button>
          ))}
        </div>
      </div>
      <PrimaryButton h={56} onClick={save} data-cta="save-card">Save</PrimaryButton>
      {plan && onRemove && <button type="button" class={s.takeOff} onClick={onRemove}>Take it off the plan</button>}
    </>
  );
}

/** "Do it later" (F_Chat / F_Today later sheet). The toast names the card: "Call the dentist: moved to tonight". */
export function LaterSheet({ card, onClose }: { card: Card | null; onClose: () => void }) {
  const pick = async (when: LaterWhen) => {
    const c = card;
    onClose();
    if (!c) return;
    const r = await laterCard(c.id, when);
    if (r) toast(`${taskShort(c.title)}: moved to ${LATER_SAID[when]}`);
  };
  return (
    <BottomSheet open={!!card} onClose={onClose} label="Do it later" handle style={PAD16}>
      <p class={s.sheetH}>Do it later</p>
      <p class={s.sheetSub}>{card?.title || ''}</p>
      <div class={s.grid2}>
        {LATER_CHIPS.map((t) => <button key={t} type="button" class={s.laterChip} onClick={() => void pick(t)}>{t}</button>)}
      </div>
    </BottomSheet>
  );
}

/** Today's cards from the chat strip: tick, open the card sheet, or Start. */
export function TodaySheet({ open, onClose, onCard }: { open: boolean; onClose: () => void; onCard: (c: Card) => void }) {
  const st = useApp((x) => x.state);
  if (!st) return null;
  const cards = st.plan.cards.filter((x) => x.st !== 'removed' && x.st !== 'moved');
  const doneN = cards.filter((x) => x.st === 'done').length;
  const next = cards.find((x) => x.st === 'todo') || null;
  return (
    <BottomSheet open={open} onClose={onClose} label="Today's cards" handle style={{ ...PAD16, paddingLeft: 'calc(14px + var(--xm-sal))', paddingRight: 'calc(14px + var(--xm-sar))', gap: 6 }}>
      <div class={s.todayHead}>
        <span class={s.todayHeadText}><span class={s.todayH}>Today</span><span class={s.todaySub}>{`${doneN} of ${cards.length} done`}</span></span>
        <button type="button" class={s.link} onClick={() => { onClose(); go('T'); }}>Open Today</button>
      </div>
      {cards.map((x) => {
        const done = x.st === 'done';
        return (
          <div key={x.id} class={s.todayRow} data-next={next && x.id === next.id ? 'true' : 'false'} data-done={done ? 'true' : 'false'}>
            <TickButton done={done} title={x.title} onTick={() => { onClose(); void tick(x, 'C'); }} />
            <button type="button" class={s.todayMain} aria-label={`Card: ${x.title}`} aria-disabled={done ? 'true' : undefined} onClick={() => { if (!done) { onClose(); onCard(x); } }}>
              <span class={s.todayTime}>{x.time}</span>
              <span class={s.todayText}><span class={s.todayTitle}>{x.title}</span><span class={s.todayMin}>{done ? 'Done' : `${x.min} min`}</span></span>
            </button>
            {!done && (
              <button type="button" class={s.playBtn} aria-label={`Start: ${x.title}`} onClick={() => { onClose(); work(x.id, null, 'C'); }}>
                <Icon name="play" size={16} color="var(--acc)" fill="var(--acc)" />
              </button>
            )}
          </div>
        );
      })}
    </BottomSheet>
  );
}
